// Survey Part 2: the Drawing (SURVEY_DRAWING_GRID_C1_WORDS.md, MOCKUP_SURVEY_DRAWING.html, approved by Robert
// 2026-10-30 8:04 AM). Pure: the rooms, the names and the Fed From lines in, a laid-out drawing out.
//
// The one rule: a line is a Fed From. Drawing one sets Fed From; picking Fed From on a card draws one. Moving a
// label never changes what feeds what. Step 1 lays everything out on its own; nothing here is saved.

/** One grid square, in drawing units (px at zoom 1). Labels, tabs and lines sit on it. */
export const GRID = 20;
/** Label height and the narrowest label. */
export const LABEL_H = 36;
export const LABEL_MIN_W = 120;
/** Grid squares from one level to the next: room for the tabs, the wire tag and the drop. */
const LEVEL = 7;
/** Grid squares between neighbors on a level. */
const GAP = 2;
/** Position tab size, in drawing units. */
export const TAB_W = 34;
export const TAB_H = 18;
/** How far a transfer switch's Primary and Emergency lines enter either side of its center. */
export const SIDE_DX = 2 * GRID;
/** The tab row hangs this far under the label. */
const TAB_DROP = 2;

/** A side of a box a line leaves or enters (DRAWING_CONNECTORS_WORDS.md). Stored readable: Top, Left, Right, Bottom. */
export type Side = "Top" | "Left" | "Right" | "Bottom";
export const SIDES: readonly Side[] = ["Top", "Left", "Right", "Bottom"];

/** One Fed From line, as the Connections row holds it. `side` is the transfer switch side: P or E, or blank. */
export interface DrawFeed {
  id: string;
  from: string;
  /** The position it leaves from on the feeder (H1, B3), or blank for the feeder itself. */
  point: string;
  to: string;
  side: "" | "P" | "E";
  /** Told or Assumed: drawn dashed. */
  notVerified: boolean;
  /** The sides picked by hand (DRAWING_CONNECTORS_WORDS.md). Blank: out the Bottom, in the Top. */
  outSide?: Side;
  inSide?: Side;
}

export interface DrawInput {
  /** Rooms in the cards' order, each with its gear in hanging order (cardRooms). */
  rooms: readonly { room: string; ids: readonly string[] }[];
  /** The tape name of each asset: "MCC-1B", "(ER3b) PNL-03". */
  names: ReadonlyMap<string, string>;
  feeds: readonly DrawFeed[];
  /** The room that sits on top (sources): "Outside". */
  topRoom: string;
  /**
   * Labels moved by hand, in grid squares (words item 17). They sit where they were put. A label not moved by hand
   * travels with its feeder when the feeder was moved, so new gear still lands under what feeds it (item 13).
   */
  placed?: ReadonlyMap<string, { x: number; y: number }>;
}

export interface DrawLabel {
  assetId: string;
  name: string;
  room: string;
  /** Top left and width, in drawing units. */
  x: number;
  y: number;
  w: number;
  /** Labeled with its source room: "(ER3b) PNL-03". Drawn dashed, as the diagram does. */
  extended: boolean;
}

export interface DrawTab {
  assetId: string;
  point: string;
  x: number;
  y: number;
  /** The side of the box the tab sits on: the side its line leaves from. */
  side: Side;
}

export interface DrawLine {
  feed: DrawFeed;
  /** Where it leaves (x1, y1) and where it enters (x2, y2). On the default sides: down to bus y, across, down. */
  x1: number;
  y1: number;
  bus: number;
  x2: number;
  y2: number;
  /** The sides it leaves and enters. */
  outSide: Side;
  inSide: Side;
  /** The whole line, square corners, start to end. */
  pts: { x: number; y: number }[];
  /** It went around a box: its shape is not the plain one. */
  around: boolean;
}

export interface DrawBand {
  room: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface Drawing {
  labels: DrawLabel[];
  tabs: DrawTab[];
  lines: DrawLine[];
  bands: DrawBand[];
  /** Gear with no Fed From and nothing it feeds, room by room (words item 12). */
  tray: { room: string; ids: string[] }[];
  /** Everything drawn: for Fit. */
  box: { x0: number; y0: number; x1: number; y1: number };
}

/** The tape grows to fit the name; nothing is cut off (standards check finding 4). Whole grid squares. */
export function labelWidth(name: string): number {
  return Math.max(LABEL_MIN_W, Math.ceil((name.length * 11 + 36) / GRID) * GRID);
}

const natural = (x: string, y: string) => x.localeCompare(y, undefined, { numeric: true });

/** Lays the drawing out (words items 11 and 13): sources on top, each level under its feeder, rooms side by side. */
export function layoutDrawing(input: DrawInput): Drawing {
  const order = new Map<string, number>();
  const roomOf = new Map<string, string>();
  const roomIndex = new Map<string, number>();
  input.rooms.forEach((r, ri) => {
    roomIndex.set(r.room, ri);
    for (const id of r.ids) {
      if (order.has(id)) continue;
      order.set(id, order.size);
      roomOf.set(id, r.room);
    }
  });
  const known = (id: string) => order.has(id);
  const feeds = input.feeds.filter((f) => known(f.from) && known(f.to) && f.from !== f.to);
  // The feed that places a label: its Primary or plain feed. An Emergency second source never moves it.
  const parentFeed = new Map<string, DrawFeed>();
  for (const f of feeds) if (f.side !== "E" && !parentFeed.has(f.to)) parentFeed.set(f.to, f);
  const kids = new Map<string, string[]>();
  for (const f of parentFeed.values()) kids.set(f.from, [...(kids.get(f.from) ?? []), f.to]);
  for (const [k, v] of kids) kids.set(k, [...new Set(v)].sort((a, b) => order.get(a)! - order.get(b)!));
  const touched = new Set<string>();
  for (const f of feeds) {
    touched.add(f.from);
    touched.add(f.to);
  }
  const nameOf = (id: string) => input.names.get(id) ?? id;
  const width = (id: string) => labelWidth(nameOf(id));
  const top = (id: string) => (roomOf.get(id) === input.topRoom ? 0 : 1);
  const byOrder = (a: string, b: string) => top(a) - top(b) || order.get(a)! - order.get(b)!;
  const roots = [...touched].filter((id) => !parentFeed.has(id)).sort(byOrder);

  const pos = new Map<string, { x: number; y: number }>();
  let col = 0;
  const place = (id: string, depth: number, seen: Set<string>): void => {
    if (pos.has(id) || seen.has(id)) return;
    seen.add(id);
    const w = width(id) / GRID;
    const ks = (kids.get(id) ?? []).filter((k) => !pos.has(k) && !seen.has(k));
    if (!ks.length) {
      pos.set(id, { x: col, y: depth * LEVEL });
      col += w + GAP;
      return;
    }
    const start = col;
    for (const k of ks) place(k, depth + 1, seen);
    const first = pos.get(ks[0]!)!;
    const lastId = ks[ks.length - 1]!;
    const last = pos.get(lastId)!;
    const mid = (first.x + last.x + width(lastId) / GRID) / 2;
    const x = Math.max(start, Math.round(mid - w / 2));
    pos.set(id, { x, y: depth * LEVEL });
    col = Math.max(col, x + w + GAP);
  };
  for (const r of roots) place(r, 0, new Set());
  // A loop in old data (Fed From never makes one) has no root: each one left starts its own column, placed once.
  for (const id of [...touched].sort(byOrder)) if (!pos.has(id)) place(id, 0, new Set());
  // Tabs on a box's left or right side widen its room, so rooms are kept apart with them counted (bands never overlap).
  const sideTab = (id: string, side: Side) => feeds.some((f) => f.from === id && f.point && f.outSide === side);
  const pad = (id: string) => ({ l: sideTab(id, "Left") ? 2 : 0, r: sideTab(id, "Right") ? 2 : 0 });
  separateRooms(pos, roomOf, input.rooms.map((r) => r.room), width, pad);
  if (input.placed?.size) applyPlaced(pos, parentFeed, input.placed);
  // Build (BUILD_LOCK_WORDS.md item 5): a label dropped on the paper stays where it was dropped, line or not. It goes
  // in last, at its saved spot, so it never shifts anything the layout placed.
  for (const [id, p] of input.placed ?? []) if (known(id) && !pos.has(id)) pos.set(id, { x: p.x, y: p.y });

  const labels: DrawLabel[] = [...pos.entries()]
    .sort((a, b) => order.get(a[0])! - order.get(b[0])!)
    .map(([id, p]) => ({ assetId: id, name: nameOf(id), room: roomOf.get(id)!, x: p.x * GRID, y: p.y * GRID, w: width(id), extended: nameOf(id).startsWith("(") }));
  const at = new Map(labels.map((l) => [l.assetId, l]));

  // Position tabs: one per position that feeds something on the drawing (item 6), on the side its line leaves from,
  // centered on that side. Under the label unless a side was picked (DRAWING_CONNECTORS_WORDS.md item 6).
  const tabs: DrawTab[] = [];
  const tabOut = new Map<string, { x: number; y: number }>();
  for (const l of labels) {
    const out = feeds.filter((f) => f.from === l.assetId && f.point && at.has(f.to));
    const sideOf = new Map<string, Side>();
    for (const f of [...out].sort((a, b) => natural(a.to, b.to))) if (!sideOf.has(f.point)) sideOf.set(f.point, f.outSide ?? "Bottom");
    // The sides this box's own lines come in on: tabs there leave the middle open for them.
    const inSides = new Set(feeds.filter((f) => f.to === l.assetId).map((f) => f.inSide ?? "Top"));
    for (const side of SIDES) {
      const pts = [...sideOf.entries()].filter(([, sd]) => sd === side).map(([p]) => p).sort(natural);
      if (!pts.length) continue;
      const across = side === "Top" || side === "Bottom";
      const span = across ? pts.length * TAB_W + (pts.length - 1) * 4 : pts.length * TAB_H + (pts.length - 1) * 4;
      const shared = inSides.has(side);
      pts.forEach((p, i) => {
        let t: DrawTab;
        if (across) {
          // Shared with a line coming in: the row starts just right of the middle.
          const x = (shared ? l.x + l.w / 2 + GRID : l.x + l.w / 2 - span / 2) + i * (TAB_W + 4);
          t = { assetId: l.assetId, point: p, x, y: side === "Bottom" ? l.y + LABEL_H + TAB_DROP : l.y - TAB_DROP - TAB_H, side };
          tabOut.set(`${l.assetId}|${p}`, { x: x + TAB_W / 2, y: side === "Bottom" ? t.y + TAB_H : t.y });
        } else {
          // Shared with a line coming in: the stack starts just under the middle.
          const y = (shared ? l.y + LABEL_H / 2 + GRID : l.y + LABEL_H / 2 - span / 2) + i * (TAB_H + 4);
          t = { assetId: l.assetId, point: p, x: side === "Left" ? l.x - TAB_DROP - TAB_W : l.x + l.w + TAB_DROP, y, side };
          tabOut.set(`${l.assetId}|${p}`, { x: side === "Left" ? t.x : t.x + TAB_W, y: y + TAB_H / 2 });
        }
        tabs.push(t);
      });
    }
  }

  // What a line must never cross: every box and every tab.
  const boxes: Box[] = [...labels.map((l) => ({ x0: l.x, y0: l.y, x1: l.x + l.w, y1: l.y + LABEL_H })), ...tabs.map((t) => ({ x0: t.x, y0: t.y, x1: t.x + TAB_W, y1: t.y + TAB_H }))];
  const lines: DrawLine[] = [];
  for (const f of feeds) {
    const a = at.get(f.from);
    const b = at.get(f.to);
    if (!a || !b) continue;
    const outSide = f.outSide ?? "Bottom";
    const inSide = f.inSide ?? "Top";
    const fromTab = f.point ? tabOut.get(`${f.from}|${f.point}`) : undefined;
    const p1 = fromTab ?? portOf(a, outSide);
    // A transfer switch takes two lines (item 21): Primary enters left of center (or above it on a side), Emergency
    // right (or below it), never one on the other.
    const off = f.side === "P" ? -1 : f.side === "E" ? 1 : 0;
    const p2 = clearOfTabs(portOf(b, inSide, off * (inSide === "Top" || inSide === "Bottom" ? SIDE_DX : 10)), b, inSide, tabs);
    const r = routeLine(p1, outSide, p2, inSide);
    // A line that would cross a box goes around it instead (the way draw.io does).
    const way = lineCrosses(r.pts, boxes) ? routeAround(p1, outSide, p2, inSide, boxes) : null;
    lines.push({ feed: f, x1: p1.x, y1: p1.y, bus: r.bus, x2: p2.x, y2: p2.y, outSide, inSide, pts: way ?? r.pts, around: way !== null });
  }

  const bands: DrawBand[] = [];
  for (const r of input.rooms) {
    const ls = labels.filter((l) => l.room === r.room);
    if (!ls.length) continue;
    bands.push(bandOf(r.room, ls, tabs));
  }

  const tray = input.rooms.map((r) => ({ room: r.room, ids: r.ids.filter((id) => !pos.has(id)) })).filter((r) => r.ids.length);
  const xs = bands.flatMap((b) => [b.x0, b.x1]);
  const ys = bands.flatMap((b) => [b.y0, b.y1]);
  const box = xs.length ? { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) } : { x0: 0, y0: 0, x1: 0, y1: 0 };
  return { labels, tabs, lines, bands, tray, box };
}

/**
 * Hand-moved labels go where they were put. Every other label keeps its place under its feeder: it moves by the same
 * amount its nearest hand-moved feeder moved. Top down, so a chain follows its head. Grid units.
 */
function applyPlaced(pos: Map<string, { x: number; y: number }>, parent: ReadonlyMap<string, DrawFeed>, placed: ReadonlyMap<string, { x: number; y: number }>): void {
  const auto = new Map([...pos].map(([id, p]) => [id, { ...p }]));
  const shift = new Map<string, { dx: number; dy: number }>();
  const shiftOf = (id: string, seen: Set<string>): { dx: number; dy: number } => {
    const have = shift.get(id);
    if (have) return have;
    const a = auto.get(id)!;
    const hand = placed.get(id);
    let d = { dx: 0, dy: 0 };
    if (hand) d = { dx: hand.x - a.x, dy: hand.y - a.y };
    else {
      const up = parent.get(id)?.from;
      if (up && pos.has(up) && !seen.has(up)) d = shiftOf(up, new Set([...seen, id]));
    }
    shift.set(id, d);
    return d;
  };
  for (const id of auto.keys()) {
    const d = shiftOf(id, new Set([id]));
    const a = auto.get(id)!;
    pos.set(id, { x: a.x + d.dx, y: a.y + d.dy });
  }
}

/** The stored shape of one hand move or an Arrange (SURVEY_DRAWING_GRID_C1_WORDS.md section H). Readable fields. */
export function drawingRow(assetId: string, x: number, y: number, byHand: boolean, s: { siteNumber: string; badgeId: string; holderName: string; deviceName: string; at: string }): Record<string, unknown> {
  return {
    "Asset ID": assetId,
    "Grid X": Math.round(x),
    "Grid Y": Math.round(y),
    "Placed By Hand": byHand ? "Yes" : "No",
    "Site Number": s.siteNumber,
    "Recorded By": s.badgeId,
    "Holder Name": s.holderName,
    "Recorded On": s.deviceName,
    "Recorded At": s.at,
  };
}

/** Hand-moved labels from stored records, later saves over earlier ones. Arrange's "No" takes a label back. */
export function placedFrom(rows: readonly Record<string, unknown>[]): Map<string, { x: number; y: number }> {
  const out = new Map<string, { x: number; y: number }>();
  const sorted = [...rows].sort((a, b) => String(a["Recorded At"] ?? "").localeCompare(String(b["Recorded At"] ?? "")));
  for (const r of sorted) {
    const id = String(r["Asset ID"] ?? "");
    const x = r["Grid X"];
    const y = r["Grid Y"];
    if (!id) continue;
    if (r["Placed By Hand"] === "Yes" && typeof x === "number" && typeof y === "number") out.set(id, { x, y });
    else out.delete(id);
  }
  return out;
}

/** A room's band: its labels and tabs, padded, room name on top. Drawing units. */
function bandOf(room: string, ls: readonly DrawLabel[], tabs: readonly DrawTab[]): DrawBand {
  const ids = new Set(ls.map((l) => l.assetId));
  const ts = tabs.filter((t) => ids.has(t.assetId));
  return {
    room,
    x0: Math.min(...ls.map((l) => l.x), ...ts.map((t) => t.x)) - GRID,
    y0: Math.min(...ls.map((l) => l.y), ...ts.map((t) => t.y)) - 2 * GRID,
    x1: Math.max(...ls.map((l) => l.x + l.w), ...ts.map((t) => t.x + TAB_W)) + GRID,
    y1: Math.max(...ls.map((l) => l.y + LABEL_H), ...ts.map((t) => t.y + TAB_H)) + GRID,
  };
}

/**
 * A line coming in on a side where the box has its own tabs enters just past them, never down through them.
 * Grid units stay whole; the box's own edge holds it.
 */
function clearOfTabs(p: { x: number; y: number }, b: DrawLabel, side: Side, tabs: readonly DrawTab[]): { x: number; y: number } {
  const ts = tabs.filter((t) => t.assetId === b.assetId && t.side === side);
  if (!ts.length) return p;
  if (side === "Top" || side === "Bottom") {
    const x0 = Math.min(...ts.map((t) => t.x));
    const x1 = Math.max(...ts.map((t) => t.x + TAB_W));
    if (p.x <= x0 - 6 || p.x >= x1 + 6) return p;
    return { x: Math.min(b.x + b.w - 6, x1 + 12), y: p.y };
  }
  const y0 = Math.min(...ts.map((t) => t.y));
  const y1 = Math.max(...ts.map((t) => t.y + TAB_H));
  if (p.y <= y0 - 6 || p.y >= y1 + 6) return p;
  return { x: p.x, y: Math.min(b.y + LABEL_H - 4, y1 + 8) };
}

/** The middle of one side of a box, nudged along that side by `along` (a transfer switch's two inputs). */
export function portOf(l: Pick<DrawLabel, "x" | "y" | "w">, side: Side, along = 0): { x: number; y: number } {
  const cx = l.x + l.w / 2;
  const cy = l.y + LABEL_H / 2;
  if (side === "Top") return { x: cx + along, y: l.y };
  if (side === "Bottom") return { x: cx + along, y: l.y + LABEL_H };
  if (side === "Left") return { x: l.x, y: cy + along };
  return { x: l.x + l.w, y: cy + along };
}

const NORMAL: Record<Side, { x: number; y: number }> = { Top: { x: 0, y: -1 }, Bottom: { x: 0, y: 1 }, Left: { x: -1, y: 0 }, Right: { x: 1, y: 0 } };

/**
 * The line builds itself (DRAWING_CONNECTORS_WORDS.md item 2): square corners, out of the side it leaves for one grid
 * square, across, and into the side it enters. Out the bottom and in the top is today's line, down to a shared bus.
 */
export function routeLine(a: { x: number; y: number }, sa: Side, b: { x: number; y: number }, sb: Side): { pts: { x: number; y: number }[]; bus: number } {
  if (sa === "Bottom" && sb === "Top") {
    const bus = b.y - 2 * GRID > a.y ? b.y - 2 * GRID : Math.round((a.y + b.y) / 2);
    return { pts: tidy([a, { x: a.x, y: bus }, { x: b.x, y: bus }, b]), bus };
  }
  const a1 = { x: a.x + NORMAL[sa].x * GRID, y: a.y + NORMAL[sa].y * GRID };
  const b1 = { x: b.x + NORMAL[sb].x * GRID, y: b.y + NORMAL[sb].y * GRID };
  const va = sa === "Top" || sa === "Bottom";
  const vb = sb === "Top" || sb === "Bottom";
  let mid: { x: number; y: number }[];
  if (va && vb) {
    // Both ends facing the same way go around the outside of both; otherwise they meet half way.
    const my = sa === sb ? (sa === "Top" ? Math.min(a1.y, b1.y) : Math.max(a1.y, b1.y)) : Math.round((a1.y + b1.y) / 2);
    mid = [{ x: a1.x, y: my }, { x: b1.x, y: my }];
  } else if (!va && !vb) {
    // Out one side toward a box behind it: run between the two rows (or under both when level), never back through a box.
    const away = sa !== sb && (sa === "Right" ? b1.x < a1.x : b1.x > a1.x);
    if (away) {
      const my = Math.abs(a1.y - b1.y) > LABEL_H + GRID ? Math.round((a1.y + b1.y) / 2) : Math.max(a1.y, b1.y) + LABEL_H / 2 + GRID;
      mid = [{ x: a1.x, y: my }, { x: b1.x, y: my }];
    } else {
      const mx = sa === sb ? (sa === "Left" ? Math.min(a1.x, b1.x) : Math.max(a1.x, b1.x)) : Math.round((a1.x + b1.x) / 2);
      mid = [{ x: mx, y: a1.y }, { x: mx, y: b1.y }];
    }
  } else if (va) mid = [{ x: a1.x, y: b1.y }];
  else mid = [{ x: b1.x, y: a1.y }];
  const pts = tidy([a, a1, ...mid, b1, b]);
  return { pts, bus: mid[0]!.y };
}

/** Drops repeated points and the middle of any three in a row on one straight line. */
function tidy(p: { x: number; y: number }[]): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  for (const q of p) {
    const last = out[out.length - 1];
    if (last && last.x === q.x && last.y === q.y) continue;
    out.push(q);
    while (out.length >= 3) {
      const [u, v, w] = out.slice(-3) as [{ x: number; y: number }, { x: number; y: number }, { x: number; y: number }];
      // Only a point that lies between its neighbors is dropped. A turn back keeps its corner.
      const between = (p: number, q: number, r: number) => (q - p) * (r - q) >= 0;
      if ((u.x === v.x && v.x === w.x && between(u.y, v.y, w.y)) || (u.y === v.y && v.y === w.y && between(u.x, v.x, w.x))) out.splice(out.length - 2, 1);
      else break;
    }
  }
  return out;
}

/** The side of a box nearest a point: where a line dropped on the box, not on a dot, goes in (words item 3). */
export function nearestSide(l: Pick<DrawLabel, "x" | "y" | "w">, p: { x: number; y: number }): Side {
  const dx = (p.x - (l.x + l.w / 2)) / (l.w / 2);
  const dy = (p.y - (l.y + LABEL_H / 2)) / (LABEL_H / 2);
  if (Math.abs(dx) > Math.abs(dy)) return dx < 0 ? "Left" : "Right";
  return dy < 0 ? "Top" : "Bottom";
}

/**
 * Room bands never overlap (standards check finding 7). Bands are taken left to right as laid out; one that runs into
 * a band already settled slides right, and everything to its right slides with it, so the tree keeps its order and
 * no line has to cross a room to reach its gear. Grid units.
 */
function separateRooms(pos: Map<string, { x: number; y: number }>, roomOf: ReadonlyMap<string, string>, rooms: readonly string[], width: (id: string) => number, pad: (id: string) => { l: number; r: number } = () => ({ l: 0, r: 0 })): void {
  const box = (room: string) => {
    const ids = [...pos.keys()].filter((id) => roomOf.get(id) === room);
    if (!ids.length) return null;
    return {
      room,
      x0: Math.min(...ids.map((id) => pos.get(id)!.x - pad(id).l)) - 1,
      x1: Math.max(...ids.map((id) => pos.get(id)!.x + width(id) / GRID + pad(id).r)) + 1,
      y0: Math.min(...ids.map((id) => pos.get(id)!.y)) - 2,
      y1: Math.max(...ids.map((id) => pos.get(id)!.y)) + 4,
    };
  };
  const settled: string[] = [];
  const left = rooms.filter((r) => box(r)).sort((a, b) => box(a)!.x0 - box(b)!.x0 || rooms.indexOf(a) - rooms.indexOf(b));
  for (const room of left) {
    for (let pass = 0; pass < 20; pass++) {
      const b = box(room)!;
      const hit = settled.map((r) => box(r)!).find((a) => b.x0 < a.x1 + 1 && a.x0 < b.x1 + 1 && b.y0 < a.y1 && a.y0 < b.y1);
      if (!hit) break;
      const dx = hit.x1 + 1 - b.x0;
      const from = b.x0;
      // This room, and anything to its right in a room not yet settled. A settled room never moves again.
      for (const [id, p] of pos) {
        const r = roomOf.get(id)!;
        if (r === room || (!settled.includes(r) && p.x >= from + 1)) p.x += dx;
      }
    }
    settled.push(room);
  }
}

/** The sides picked for the lines into one box (DRAWING_CONNECTORS_WORDS.md item 7). Blank: the default. */
export interface BoxSides {
  feedOut: Side | "";
  feedIn: Side | "";
  emergencyOut: Side | "";
  emergencyIn: Side | "";
}

/** The readable stored names of the four side fields, on the fed box's drawing record. */
export const SIDE_FIELDS: Record<keyof BoxSides, string> = { feedOut: "Feed Out Side", feedIn: "Feed In Side", emergencyOut: "Emergency Out Side", emergencyIn: "Emergency In Side" };

const asSide = (v: unknown): Side | "" => (SIDES as readonly unknown[]).includes(v) ? (v as Side) : "";

/** The sides saved for each box, from stored drawing records; a later save of a field wins over an earlier one. */
export function sidesFrom(rows: readonly Record<string, unknown>[]): Map<string, BoxSides> {
  const out = new Map<string, BoxSides>();
  const sorted = [...rows].sort((a, b) => String(a["Recorded At"] ?? "").localeCompare(String(b["Recorded At"] ?? "")));
  for (const r of sorted) {
    const id = String(r["Asset ID"] ?? "");
    if (!id) continue;
    const cur = out.get(id) ?? { feedOut: "", feedIn: "", emergencyOut: "", emergencyIn: "" };
    for (const k of Object.keys(SIDE_FIELDS) as (keyof BoxSides)[]) if (SIDE_FIELDS[k] in r) cur[k] = asSide(r[SIDE_FIELDS[k]]);
    out.set(id, cur);
  }
  for (const [id, v] of out) if (!v.feedOut && !v.feedIn && !v.emergencyOut && !v.emergencyIn) out.delete(id);
  return out;
}

/** The stored fields for a box's sides. The default (out the Bottom, in the Top) saves blank. */
export function sideFields(v: BoxSides): Record<string, string> {
  const f: Record<string, string> = {};
  for (const k of Object.keys(SIDE_FIELDS) as (keyof BoxSides)[]) f[SIDE_FIELDS[k]] = v[k];
  return f;
}

// ---- Routing around boxes (Robert 2026-10-30 1:20 PM: a line ran through SWBD-01) ----
//
// The way draw.io and libavoid route orthogonal connectors: lanes run one grid square outside every box, a shortest
// path over those lanes costs its length plus a little for each turn, and it never enters a box. Used only when the
// plain line would cross a box, so the everyday lines keep their shape.

export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Does a straight, square run pass through the inside of a box? Touching an edge does not count. */
export function runCrosses(p: { x: number; y: number }, q: { x: number; y: number }, r: Box): boolean {
  if (p.y === q.y) return p.y > r.y0 && p.y < r.y1 && Math.max(p.x, q.x) > r.x0 && Math.min(p.x, q.x) < r.x1;
  if (p.x === q.x) return p.x > r.x0 && p.x < r.x1 && Math.max(p.y, q.y) > r.y0 && Math.min(p.y, q.y) < r.y1;
  return false;
}

/** Does a line pass through any box? */
export function lineCrosses(pts: readonly { x: number; y: number }[], boxes: readonly Box[]): boolean {
  for (let i = 1; i < pts.length; i++) for (const r of boxes) if (runCrosses(pts[i - 1]!, pts[i]!, r)) return true;
  return false;
}

const DIRS = [{ x: 1, y: 0 }, { x: -1, y: 0 }, { x: 0, y: 1 }, { x: 0, y: -1 }];
const dirOf = (v: { x: number; y: number }) => DIRS.findIndex((d) => d.x === Math.sign(v.x) && d.y === Math.sign(v.y));
const TURN = 2 * GRID;

/**
 * A square-cornered line from a (leaving side sa) to b (entering side sb) that goes around every box. Null when no way
 * round is found inside the search area; the caller then keeps the plain line.
 */
export function routeAround(a: { x: number; y: number }, sa: Side, b: { x: number; y: number }, sb: Side, boxes: readonly Box[]): { x: number; y: number }[] | null {
  const a1 = { x: a.x + NORMAL[sa].x * GRID, y: a.y + NORMAL[sa].y * GRID };
  const b1 = { x: b.x + NORMAL[sb].x * GRID, y: b.y + NORMAL[sb].y * GRID };
  const m = 6 * GRID;
  const area = { x0: Math.min(a1.x, b1.x) - m, y0: Math.min(a1.y, b1.y) - m, x1: Math.max(a1.x, b1.x) + m, y1: Math.max(a1.y, b1.y) + m };
  // Boxes grown by half a square: lanes at one square outside them never touch them.
  const half = GRID / 2;
  const obs = boxes.filter((r) => r.x1 > area.x0 && r.x0 < area.x1 && r.y1 > area.y0 && r.y0 < area.y1).map((r) => ({ x0: r.x0 - half, y0: r.y0 - half, x1: r.x1 + half, y1: r.y1 + half }));
  const xs = [...new Set([a1.x, b1.x, Math.round((a1.x + b1.x) / 2), area.x0, area.x1, ...obs.flatMap((r) => [r.x0 - half, r.x1 + half])])].sort((p, q) => p - q);
  const ys = [...new Set([a1.y, b1.y, Math.round((a1.y + b1.y) / 2), area.y0, area.y1, ...obs.flatMap((r) => [r.y0 - half, r.y1 + half])])].sort((p, q) => p - q);
  const W = xs.length;
  const H = ys.length;
  const inside = (x: number, y: number) => obs.some((r) => x > r.x0 && x < r.x1 && y > r.y0 && y < r.y1);
  const free = new Uint8Array(W * H);
  for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) free[j * W + i] = inside(xs[i]!, ys[j]!) ? 0 : 1;
  const ai = xs.indexOf(a1.x);
  const aj = ys.indexOf(a1.y);
  const bi = xs.indexOf(b1.x);
  const bj = ys.indexOf(b1.y);
  if (!free[aj * W + ai] || !free[bj * W + bi]) return null;
  const startDir = dirOf(NORMAL[sa]);
  const endDir = dirOf({ x: -NORMAL[sb].x, y: -NORMAL[sb].y });
  // Shortest path over (lane crossing, heading). A small binary heap keeps it quick on a big site.
  const N = W * H * 4;
  const cost = new Float64Array(N).fill(Infinity);
  const prev = new Int32Array(N).fill(-1);
  const heap: [number, number][] = [];
  const push = (c: number, st: number) => {
    heap.push([c, st]);
    let k = heap.length - 1;
    while (k > 0) {
      const up = (k - 1) >> 1;
      if (heap[up]![0] <= heap[k]![0]) break;
      [heap[up], heap[k]] = [heap[k]!, heap[up]!];
      k = up;
    }
  };
  const pop = (): [number, number] => {
    const top = heap[0]!;
    const last = heap.pop()!;
    if (heap.length) {
      heap[0] = last;
      let k = 0;
      for (;;) {
        const l = 2 * k + 1;
        const r = l + 1;
        let s = k;
        if (l < heap.length && heap[l]![0] < heap[s]![0]) s = l;
        if (r < heap.length && heap[r]![0] < heap[s]![0]) s = r;
        if (s === k) break;
        [heap[s], heap[k]] = [heap[k]!, heap[s]!];
        k = s;
      }
    }
    return top;
  };
  const start = (aj * W + ai) * 4 + startDir;
  cost[start] = 0;
  push(0, start);
  let best = -1;
  let bestCost = Infinity;
  while (heap.length) {
    const [c, st] = pop();
    if (c > cost[st]!) continue;
    if (c >= bestCost) break;
    const node = st >> 2;
    const dir = st & 3;
    const i = node % W;
    const j = (node - i) / W;
    if (i === bi && j === bj) {
      const total = c + (dir === endDir ? 0 : TURN);
      if (dir !== (endDir ^ 1) && total < bestCost) {
        bestCost = total;
        best = st;
      }
      continue;
    }
    for (let nd = 0; nd < 4; nd++) {
      if (nd === (dir ^ 1)) continue;
      const ni = i + DIRS[nd]!.x;
      const nj = j + DIRS[nd]!.y;
      if (ni < 0 || nj < 0 || ni >= W || nj >= H || !free[nj * W + ni]) continue;
      const p = { x: xs[i]!, y: ys[j]! };
      const q = { x: xs[ni]!, y: ys[nj]! };
      if (obs.some((r) => runCrosses(p, q, r))) continue;
      const nc = c + Math.abs(q.x - p.x) + Math.abs(q.y - p.y) + (nd === dir ? 0 : TURN);
      const ns = (nj * W + ni) * 4 + nd;
      if (nc < cost[ns]!) {
        cost[ns] = nc;
        prev[ns] = st;
        push(nc, ns);
      }
    }
  }
  if (best < 0) return null;
  const path: { x: number; y: number }[] = [];
  for (let st = best; st >= 0; st = prev[st]!) {
    const node = st >> 2;
    const i = node % W;
    path.push({ x: xs[i]!, y: ys[(node - i) / W]! });
  }
  path.reverse();
  return tidy([a, ...path, b]);
}
